/**
 * SchemaCard Component
 *
 * Card displaying a multi-field Schema with field count, status, and action buttons.
 * Shows field names, version, status badge, and quick actions (Edit, Publish, Delete).
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Schema, SCHEMA_STATUSES } from '@/services/api/schemaApi';
import { Pencil, Trash2, Send, Archive, Layers, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SchemaCardProps {
  schema: Schema;
  /** Optional human-readable owning-org name (e.g. "Garcia House"). Only
      populated by AdminSchemasTab when an org lookup is available. */
  orgName?: string;
  onEdit: () => void;
  onDelete: () => void;
  onPublish: () => void;
  onDeprecate: () => void;
}

export const SchemaCard: React.FC<SchemaCardProps> = ({
  schema,
  orgName,
  onEdit,
  onDelete,
  onPublish,
  onDeprecate,
}) => {
  const statusConfig = SCHEMA_STATUSES.find((s) => s.value === schema.status) || SCHEMA_STATUSES[0];
  const fieldCount = schema.fields?.length || 0;

  // Get first few field names for preview
  const fieldPreview = schema.fields?.slice(0, 4).map((f) => f.name).join(', ') || '';
  const hasMoreFields = fieldCount > 4;

  // Get type distribution for badge
  const getTypeDistribution = () => {
    if (!schema.fields || schema.fields.length === 0) return '';
    const typeCounts: Record<string, number> = {};
    schema.fields.forEach((f) => {
      typeCounts[f.type] = (typeCounts[f.type] || 0) + 1;
    });
    return Object.entries(typeCounts)
      .map(([type, count]) => `${count} ${type}`)
      .join(', ');
  };

  return (
    <div className="border border-border rounded-lg p-4 bg-background hover:bg-accent/30 transition-colors">
      <div className="flex items-start justify-between">
        {/* Schema Info */}
        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-center gap-2 flex-wrap">
            {/* Field count badge */}
            <span className="flex items-center gap-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-0.5 rounded">
              <Layers className="h-3 w-3" />
              {fieldCount} field{fieldCount !== 1 ? 's' : ''}
            </span>

            {/* Schema name */}
            <h4 className="font-medium truncate">{schema.displayName || schema.name}</h4>

            {/* Status badge */}
            <span
              className={cn(
                'text-xs font-medium px-2 py-0.5 rounded',
                statusConfig.color
              )}
            >
              {statusConfig.label}
            </span>

            {/* Owning org badge — distinguishes schemas with the same name
                across different orgs (e.g. Garcia House's water_depth vs
                George's water_depth). */}
            {orgName && (
              <span
                className="flex items-center gap-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-0.5 rounded"
                title={`Owned by ${orgName}`}
              >
                <Building2 className="h-3 w-3" />
                {orgName}
              </span>
            )}
          </div>

          {/* Technical name and version */}
          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
            <span className="font-mono">{schema.name}</span>
            <span>•</span>
            <span>v{schema.currentVersion}</span>
            {schema.latestPublishedVersion > 0 && schema.latestPublishedVersion !== schema.currentVersion && (
              <>
                <span>•</span>
                <span>Published: v{schema.latestPublishedVersion}</span>
              </>
            )}
          </div>

          {/* Description */}
          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
            {schema.description || 'No description'}
          </p>

          {/* Fields preview */}
          {fieldCount > 0 && (
            <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground/80">Fields:</span>
              <span className="font-mono text-xs">
                {fieldPreview}
                {hasMoreFields && ` +${fieldCount - 4} more`}
              </span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0 ml-4">
          {/* Publish button - only for drafts */}
          {statusConfig.value === 'draft' && (
            <Button
              variant="outline"
              size="sm"
              onClick={onPublish}
              title="Publish schema"
              className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
            >
              <Send className="h-4 w-4 mr-1" />
              Publish
            </Button>
          )}

          {/* Deprecate button - only for published */}
          {statusConfig.value === 'published' && (
            <Button
              variant="outline"
              size="sm"
              onClick={onDeprecate}
              title="Deprecate schema"
              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20"
            >
              <Archive className="h-4 w-4 mr-1" />
              Deprecate
            </Button>
          )}

          {/* Edit button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onEdit}
            aria-label="Edit schema"
            title="Edit schema"
          >
            <Pencil className="h-4 w-4" />
          </Button>

          {/* Delete button - only for drafts */}
          {statusConfig.value === 'draft' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              aria-label="Delete schema"
              title="Delete schema"
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchemaCard;
